import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import MenuScreen from '@/components/MenuScreen';
import JoinRoom from '@/components/JoinRoom';
import Lobby from '@/components/Lobby';
import GameRoom from '@/components/GameRoom';
import GameEnd from '@/components/GameEnd';
import { MusicControl } from '@/components/MusicControl';
import { useGameStore } from '@/lib/gameStore';
import { supabase } from '@/integrations/supabase/client';
import { useBackgroundMusic } from '@/hooks/useBackgroundMusic';

type Screen = 'menu' | 'join' | 'lobby' | 'game' | 'end';

const Index = () => {
  const [currentScreen, setCurrentScreen] = useState<Screen>('menu');
  const { reset, currentRoom, playerId, playerName } = useGameStore();
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const { start, stop, setIntensity, setVolume } = useBackgroundMusic();
  const [sessionRecovered, setSessionRecovered] = useState(false);

  // Start music on first user interaction
  useEffect(() => {
    const handleStart = () => {
      start();
      document.removeEventListener('click', handleStart);
      document.removeEventListener('keydown', handleStart);
    };

    document.addEventListener('click', handleStart);
    document.addEventListener('keydown', handleStart);

    return () => {
      document.removeEventListener('click', handleStart);
      document.removeEventListener('keydown', handleStart);
      stop();
    };
  }, [start, stop]);

  // Session recovery - check for existing persisted session
  useEffect(() => {
    if (sessionRecovered || !currentRoom || !playerId) return;

    const recoverSession = async () => {
      try {
        // Verify auth session is still valid
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          reset();
          return;
        }

        // Verify room still exists
        const { data: room, error: roomError } = await supabase
          .from('rooms')
          .select('*')
          .eq('id', currentRoom.id)
          .single();

        if (roomError || !room) {
          reset();
          return;
        }

        // Verify player record still exists
        const { data: player, error: playerError } = await supabase
          .from('players')
          .select('*')
          .eq('id', playerId)
          .single();

        if (playerError || !player) {
          reset();
          return;
        }

        // Auto-redirect based on room status
        if (room.status === 'playing') {
          setCurrentScreen('game');
        } else if (room.status === 'lobby') {
          setCurrentScreen('lobby');
        } else if (room.status === 'finished') {
          setCurrentScreen('end');
        }

        setSessionRecovered(true);
      } catch (error) {
        console.error('Session recovery error:', error);
        reset();
      }
    };

    recoverSession();
  }, [currentRoom, playerId, reset, sessionRecovered]);

  // Handle deep linking
  useEffect(() => {
    if (roomCode && !sessionRecovered) {
      setCurrentScreen('join');
    }
  }, [roomCode, sessionRecovered]);

  // Subscribe to room status changes for automatic navigation
  useEffect(() => {
    if (!currentRoom) return;

    const channel = supabase
      .channel(`index-room:${currentRoom.id}`)
      .on(
        'postgres_changes',
        {
          event: 'UPDATE',
          schema: 'public',
          table: 'rooms',
          filter: `id=eq.${currentRoom.id}`
        },
        (payload) => {
          const newRoom = payload.new as any;
          
          // Only handle game->end transition here
          // Lobby->game transition is handled by Lobby component
          if (newRoom.status === 'finished' && currentScreen === 'game') {
            setCurrentScreen('end');
          }
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [currentRoom?.id, currentScreen]);

  const handleNavigate = (screen: Screen) => {
    setCurrentScreen(screen);
  };

  const handleLeave = () => {
    reset();
    navigate('/');
    setCurrentScreen('menu');
  };

  return (
    <div className="min-h-screen bg-background">
      {currentScreen === 'menu' && (
        <MenuScreen onNavigate={handleNavigate} setMusicIntensity={setIntensity} />
      )}
      
      {currentScreen === 'join' && (
        <JoinRoom onNavigate={handleNavigate} initialRoomCode={roomCode} />
      )}
      
      {currentScreen === 'lobby' && (
        <Lobby 
          onStartGame={() => handleNavigate('game')} 
          onLeave={handleLeave}
          setMusicIntensity={setIntensity}
        />
      )}

      {currentScreen === 'game' && (
        <GameRoom 
          onGameEnd={() => handleNavigate('end')} 
          setMusicIntensity={setIntensity}
        />
      )}

      {currentScreen === 'end' && (
        <GameEnd 
          onPlayAgain={() => handleNavigate('lobby')}
          onBackToMenu={handleLeave}
          setMusicIntensity={setIntensity}
        />
      )}

      <MusicControl onVolumeChange={setVolume} />
    </div>
  );
};

export default Index;
